import http from './http';
import { redactError } from '../utils/redactError';

import { createLogger } from '../utils/logger';

const logger = createLogger('MedicalEquipment');
export interface MedicalEquipment {
  id: string;
  userId: string;
  taskId: number;
  equipmentType: string;
  companyName: string;
  location: string;
  phone: string;
  email: string;
  state: string;
  notes: string;
  rentalCost?: string;
  startDate?: string;
  contractEnd?: string;
  contactPerson?: string;
  maintenanceSchedule?: string;
  serialNumber?: string;
  modelNumber?: string;
  isCompleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateMedicalEquipmentData {
  taskId: number;
  equipmentType?: string;
  companyName?: string;
  location?: string;
  phone?: string;
  email?: string;
  state?: string;
  notes?: string;
  rentalCost?: string;
  startDate?: string;
  contractEnd?: string;
  contactPerson?: string;
  maintenanceSchedule?: string;
  serialNumber?: string;
  modelNumber?: string;
}

export interface UpdateMedicalEquipmentData extends Partial<CreateMedicalEquipmentData> {
  isCompleted?: boolean;
  additionalData?: Record<string, any>;
}

class MedicalEquipmentService {
  async getMedicalEquipment(taskId: number, targetUserId?: string): Promise<MedicalEquipment[]> {
    try {
      const params = targetUserId ? `?taskId=${taskId}&targetUserId=${targetUserId}` : `?taskId=${taskId}`;
      logger.debug(`=== MEDICAL EQUIPMENT SERVICE: Final URL = ${http.defaults.baseURL}/api/medical-equipment${params} ===`);
      const response = await http.get(`/api/medical-equipment${params}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching medical equipment:', redactError(error));
      throw new Error(`Failed to fetch medical equipment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async createMedicalEquipment(data: CreateMedicalEquipmentData): Promise<{ id: string }> {
    try {
      logger.debug(`=== MEDICAL EQUIPMENT SERVICE: Final URL = ${http.defaults.baseURL}/api/medical-equipment ===`);
      const response = await http.post('/api/medical-equipment', data);
      return response.data;
    } catch (error) {
      logger.error('Error creating medical equipment:', redactError(error));
      throw new Error(`Failed to create medical equipment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async updateMedicalEquipment(id: string, form: UpdateMedicalEquipmentData): Promise<MedicalEquipment> {
    try {
      // Build minimal payload containing only fields we intend to update
      const payload: Record<string, any> = {};
      
      // Only include fields that are explicitly being updated
      if (form.equipmentType !== undefined) payload.equipmentType = form.equipmentType;
      if (form.companyName !== undefined) payload.companyName = form.companyName;
      if (form.location !== undefined) payload.location = form.location;
      if (form.phone !== undefined) payload.phone = form.phone;
      if (form.email !== undefined) payload.email = form.email;
      if (form.state !== undefined) payload.state = form.state;
      if (form.notes !== undefined) payload.notes = form.notes;
      if (form.rentalCost !== undefined) payload.rentalCost = form.rentalCost;
      if (form.startDate !== undefined) payload.startDate = form.startDate;
      if (form.contractEnd !== undefined) payload.contractEnd = form.contractEnd;
      if (form.contactPerson !== undefined) payload.contactPerson = form.contactPerson;
      if (form.maintenanceSchedule !== undefined) payload.maintenanceSchedule = form.maintenanceSchedule;
      if (form.serialNumber !== undefined) payload.serialNumber = form.serialNumber;
      if (form.modelNumber !== undefined) payload.modelNumber = form.modelNumber;
      
      // These are system-managed fields that can be updated programmatically
      if (form.isCompleted !== undefined) payload.isCompleted = form.isCompleted;
      if (form.additionalData !== undefined) payload.additionalData = form.additionalData;

      logger.debug('PATCH URL:', `/api/medical-equipment/${id}`);
      logger.debug('PATCH BODY:', payload);

      const response = await http.patch(`/api/medical-equipment/${id}`, payload);
      return response.data;
    } catch (error: any) {
      logger.error('Error updating medical equipment:', redactError(error));
      throw new Error(`Failed to update medical equipment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async deleteMedicalEquipment(id: string): Promise<void> {
    try {
      await http.delete(`/api/medical-equipment/${id}`);
    } catch (error) {
      logger.error('Error deleting medical equipment:', redactError(error));
      throw new Error(`Failed to delete medical equipment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async markAsCompleted(taskId: number): Promise<void> {
    try {
      const equipment = await this.getMedicalEquipment(taskId);
      if (equipment && equipment.length > 0) {
        for (const item of equipment) {
          await this.updateMedicalEquipment(item.id, { isCompleted: true });
        }
      }
    } catch (error) {
      logger.error('Error marking medical equipment as completed:', redactError(error));
      throw error;
    }
  }

  async lockTaskSharing(taskId: number): Promise<void> {
    try {
      const equipment = await this.getMedicalEquipment(taskId);
      if (equipment && equipment.length > 0) {
        for (const item of equipment) {
          await this.updateMedicalEquipment(item.id, { additionalData: { visibilityState: 'LOCKED' } });
        }
      }
    } catch (error) {
      logger.error('Error locking medical equipment sharing:', redactError(error));
      throw error;
    }
  }

  async unlockTaskSharing(taskId: number): Promise<void> {
    try {
      const equipment = await this.getMedicalEquipment(taskId);
      if (equipment && equipment.length > 0) {
        for (const item of equipment) {
          await this.updateMedicalEquipment(item.id, { additionalData: { visibilityState: 'UNLOCKED' } });
        }
      }
    } catch (error) {
      logger.error('Error unlocking medical equipment sharing:', redactError(error));
      throw error;
    }
  }

  async lockMedicalEquipment(id: string): Promise<void> {
    try {
      logger.debug(`Locking medical equipment ${id}`);
      await http.post(`/api/medical-equipment/${id}/lock`);
    } catch (error) {
      logger.error('Error locking medical equipment:', redactError(error));
      throw new Error(`Failed to lock medical equipment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async unlockMedicalEquipment(id: string, selectedEmails: string[]): Promise<void> {
    try {
      logger.debug(`Unlocking medical equipment ${id} with emails:`, selectedEmails);
      await http.post(`/api/medical-equipment/${id}/unlock`, { selectedEmails });
    } catch (error) {
      logger.error('Error unlocking medical equipment:', redactError(error));
      throw new Error(`Failed to unlock medical equipment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getMedicalEquipmentAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/medical-equipment/${id}/access-grants`);
      return response.data?.data || response.data || [];
    } catch (error) {
      logger.error('Error fetching medical equipment access grants:', redactError(error));
      return [];
    }
  }
}

export const medicalEquipmentService = new MedicalEquipmentService();